import type { Appearance } from '@stripe/stripe-js';

/**
 * Stripe's PaymentElement renders in a cross-origin iframe, so it cannot inherit our CSS
 * custom properties — the appearance object has to be handed literal values. Reading them
 * back off `:root` at build time keeps `globals.css` the single source of truth, so the
 * card form follows a token change (and a future dark mode, §10) like everything else
 * rather than drifting on its own hardcoded palette.
 */
function readTokens(): Record<string, string> {
  // jsdom and SSR have no layout, and `getComputedStyle` on a detached root returns empty
  // strings rather than throwing; the `||` fallbacks below cover that.
  const styles = getComputedStyle(document.documentElement);
  const read = (name: string) => styles.getPropertyValue(name).trim();

  return {
    primary: read('--primary'),
    foreground: read('--foreground'),
    mutedForeground: read('--muted-foreground'),
    subtle: read('--subtle'),
    destructive: read('--destructive'),
    border: read('--border'),
    background: read('--background'),
    surface2: read('--surface-2'),
    disabled: read('--disabled'),
    radius: read('--radius'),
    fontSans: read('--font-sans'),
  };
}

/** §6 control metrics: height 38, radius 10, 12px side padding, 14px text. */
export function buildStripeAppearance(): Appearance {
  const token = readTokens();

  return {
    // §5: the dialog animates, its contents do not.
    disableAnimations: true,
    variables: {
      colorPrimary: token.primary || '#0E8F94',
      colorBackground: token.background || '#FFFFFF',
      colorText: token.foreground || '#0B0B0C',
      colorTextSecondary: token.mutedForeground || '#6B7076',
      colorTextPlaceholder: token.disabled || '#B4BAC0',
      colorDanger: token.destructive || '#A11B1B',
      fontFamily: token.fontSans || '"Golos Text", system-ui, sans-serif',
      borderRadius: token.radius || '0.625rem',
      fontSizeBase: '14px',
      fontSizeSm: '13px',
      fontSize3Xs: '12.5px',
      spacingUnit: '4px',
      spacingGridRow: '16px',
    },
    rules: {
      '.Input': {
        border: `1px solid ${token.border || '#E4E6E9'}`,
        boxShadow: 'none',
        padding: '9px 12px',
        lineHeight: '1.2',
      },
      // §6: teal border plus the 3px 14% ring. Stripe has no ring primitive, so the ring
      // is drawn as the box-shadow — the same value `--ring-shadow` holds.
      '.Input:focus': {
        border: `1px solid ${token.primary || '#0E8F94'}`,
        boxShadow: '0 0 0 3px rgba(14, 143, 148, 0.14)',
        outline: 'none',
      },
      '.Input--invalid': {
        border: `1px solid ${token.destructive || '#A11B1B'}`,
        boxShadow: 'none',
      },
      '.Input--invalid:focus': {
        boxShadow: '0 0 0 3px rgba(161, 27, 27, 0.12)',
      },
      '.Input:disabled': {
        backgroundColor: token.surface2 || '#F7F8F9',
        color: token.disabled || '#B4BAC0',
      },
      // §3 field label: 12.5 / 400 / muted, 6–7px above its control.
      '.Label': {
        fontSize: '12.5px',
        fontWeight: '400',
        color: token.mutedForeground || '#6B7076',
        marginBottom: '6px',
      },
      '.Error': {
        fontSize: '12px',
        marginTop: '6px',
      },
      '.Tab': {
        border: `1px solid ${token.border || '#E4E6E9'}`,
        boxShadow: 'none',
      },
      '.Tab:hover': {
        color: token.foreground || '#0B0B0C',
      },
      '.Tab--selected': {
        border: `1px solid ${token.primary || '#0E8F94'}`,
        boxShadow: 'none',
      },
      '.TabLabel': {
        fontWeight: '600',
      },
    },
  };
}
